import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import DataCollection from '../../shared/DataCollection';
import { WeightLotDropdown } from '../lots/WeightLotDropdown.js';
import WeightAnimalsCardList from './WeightAnimalsCardList.js';
import { Fetch } from '../../utils/Utils.js';
import { compareWeighins } from '../weighins.js';
import '../../herd/ListHerd.css';
import '../lots/WeightLots.css';

export const ListWeightAnimals = ({ initialLotFilter }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [refreshKey, setRefreshKey] = useState(0);
  const [lots, setLots] = useState([]);
  const [latestWeights, setLatestWeights] = useState({});
  const [firstWeights,  setFirstWeights]  = useState({});
  const [weighInCounts, setWeighInCounts] = useState({});
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [searchFields, setSearchFields] = useState(() =>
    initialLotFilter ? { lot_id: String(initialLotFilter) } : {}
  );
  const [settingsLoaded, setSettingsLoaded] = useState(!!initialLotFilter);

  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  // Seed the lot filter from the default lot unless a specific lot was already provided.
  useEffect(() => {
    if (initialLotFilter) return;
    Fetch('api/ranch', 'get', null, (data) => {
      if (data?.default_weight_lot) setSearchFields({ lot_id: String(data.default_weight_lot) });
      setSettingsLoaded(true);
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (initialLotFilter) {
      setSearchFields({ lot_id: String(initialLotFilter) });
      setRefreshKey(k => k + 1);
    }
  }, [initialLotFilter]);

  useEffect(() => {
    Fetch('api/data/lots?search_fields=type&search_values=weight', 'get', null, (data) => {
      setLots([...(data || [])].sort((a, b) => b.lot_id - a.lot_id));
    });
  }, [refreshKey]);

  useEffect(() => {
    if (!settingsLoaded) return;
    const lotQuery = searchFields.lot_id
      ? `?search_fields=lot_id&search_values=${searchFields.lot_id}`
      : '';
    Fetch(`api/data/weights${lotQuery}`, 'get', null, (data) => {
      const latestMap = {};
      const firstMap  = {};
      const countMap  = {};
      (data || []).forEach(w => {
        if (!latestMap[w.tag] || compareWeighins(w, latestMap[w.tag]) > 0) latestMap[w.tag] = w;
        if (!firstMap[w.tag]  || compareWeighins(w, firstMap[w.tag])  < 0) firstMap[w.tag]  = w;
        countMap[w.tag] = (countMap[w.tag] || 0) + 1;
      });
      setLatestWeights(latestMap);
      setFirstWeights(firstMap);
      setWeighInCounts(countMap);
    });
  }, [refreshKey, settingsLoaded, searchFields.lot_id]); // eslint-disable-line react-hooks/exhaustive-deps

  const onSearchLot = (e) => {
    const value = e.target.value;
    setSearchFields(prev => {
      const next = { ...prev };
      if (value) { next.lot_id = value; } else { delete next.lot_id; }
      return next;
    });
  };

  const onSearchTag = (e) => {
    const value = e.target.value;
    setSearchFields(prev => {
      const next = { ...prev };
      if (value) { next.tag = value; } else { delete next.tag; }
      return next;
    });
  };

  const activeFilterCount = [searchFields.lot_id].filter(Boolean).length;

  const resetFilters = () => {
    setSearchFields(prev => {
      const next = { ...prev };
      delete next.lot_id;
      return next;
    });
    setRefreshKey(k => k + 1);
  };

  const formatShortDate = (dateString) => {
    if (!dateString) return '—';
    const s = dateString.length === 10 ? dateString + 'T12:00:00' : dateString;
    const d = new Date(s);
    if (isNaN(d)) return dateString;
    return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getFullYear()).slice(2)}`;
  };

  const lotById = Object.fromEntries((lots || []).map(l => [l.lot_id, l]));

  const fmtLotCode = (lotId) => {
    const lot  = lotById[lotId];
    const code = `WEIGHT-${String(lotId).padStart(4, '0')}`;
    return lot?.name ? `${code} — ${lot.name}` : code;
  };

  const columns = [
    {
      name: t('tag'),
      selector: row => row.tag,
      sortable: true,
      cell: row => (
        <span className="wl-lot-link" onClick={() => navigate(`/weight/animals/${row.tag}`)}>
          {row.tag}
        </span>
      ),
    },
    {
      name: t('lot'),
      selector: row => row.lot_id,
      sortable: true,
      cell: row => row.lot_id ? fmtLotCode(row.lot_id) : '—',
    },
    {
      name: t('first_weigh_date'),
      selector: row => firstWeights[row.tag]?.date ?? null,
      sortable: true,
      cell: row => formatShortDate(firstWeights[row.tag]?.date),
    },
    {
      name: t('first_weigh_value'),
      selector: row => firstWeights[row.tag]?.weight ?? null,
      sortable: true,
      cell: row => firstWeights[row.tag] ? `${firstWeights[row.tag].weight} kg` : '—',
    },
    {
      name: t('last_weigh_date'),
      selector: row => latestWeights[row.tag]?.date ?? null,
      sortable: true,
      cell: row => formatShortDate(latestWeights[row.tag]?.date),
    },
    {
      name: t('last_weigh_value'),
      selector: row => latestWeights[row.tag]?.weight ?? null,
      sortable: true,
      cell: row => latestWeights[row.tag] ? `${latestWeights[row.tag].weight} kg` : '—',
    },
    {
      name: t('adg_column'),
      selector: row => {
        const first = firstWeights[row.tag];
        const last  = latestWeights[row.tag];
        if (!first || !last) return null;
        const days = (new Date(last.date) - new Date(first.date)) / 86400000;
        return days > 0 ? (last.weight - first.weight) / days : null;
      },
      sortable: true,
      cell: row => {
        const first = firstWeights[row.tag];
        const last  = latestWeights[row.tag];
        if (!first || !last) return '—';
        const days = (new Date(last.date) - new Date(first.date)) / 86400000;
        if (days <= 0) return '—';
        return `${((last.weight - first.weight) / days).toFixed(2)} kg/d`;
      },
    },
  ];

  return (
    <div className="ListWeightAnimals">
      {isMobile ? (
        <div className="mobile-search-filter">
          <input
            className="mobile-search-input"
            type="text"
            placeholder={t('find_tag')}
            onChange={onSearchTag}
          />
          <button
            className={`mobile-filter-toggle ${filtersOpen ? 'open' : ''} ${activeFilterCount > 0 ? 'active' : ''}`}
            onClick={() => setFiltersOpen(o => !o)}
          >
            {t('filters')}
            {activeFilterCount > 0 && <span className="mobile-filter-badge">{activeFilterCount}</span>}
          </button>
        </div>
      ) : (
        <div className="desktop-filter-bar">
          <div className="desktop-filter-top">
            <input
              type="text"
              className="desktop-search-input"
              placeholder={t('find_tag')}
              onChange={onSearchTag}
            />
            <WeightLotDropdown
              showAll={true}
              lots={lots}
              selected={searchFields.lot_id || ''}
              onChange={onSearchLot}
            />
          </div>
        </div>
      )}

      {settingsLoaded && <DataCollection
        collection="lots.animals"
        columns={columns}
        key={refreshKey}
        searchFields={{ type: 'weight', ...searchFields }}
        mobileComponent={(data) => (
          <WeightAnimalsCardList
            data={data}
            latestWeights={latestWeights}
            weighInCounts={weighInCounts}
            lotById={lotById}
            fmtLotCode={fmtLotCode}
          />
        )}
      />}

      {filtersOpen && (
        <>
          <div className="filter-sheet-backdrop" onClick={() => setFiltersOpen(false)} />
          <div className="filter-sheet">
            <div className="filter-sheet-handle" />
            <div className="filter-sheet-header">
              <span className="filter-sheet-title">{t('filters')}</span>
              <button className="filter-sheet-close" onClick={() => setFiltersOpen(false)}>✕</button>
            </div>
            <div className="filter-sheet-body">
              <WeightLotDropdown
                showAll={true}
                lots={lots}
                selected={searchFields.lot_id || ''}
                onChange={onSearchLot}
              />
            </div>
            <div className="filter-sheet-footer">
              <button
                className="filter-sheet-reset"
                onClick={resetFilters}
                disabled={activeFilterCount === 0}
              >
                {t('reset_filters')} {activeFilterCount > 0 && `(${activeFilterCount})`}
              </button>
              <button className="filter-sheet-apply" onClick={() => setFiltersOpen(false)}>
                {t('done')}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ListWeightAnimals;
